import { useState } from 'react'
import { NAVIGATION } from '@/app/navigation'
import type { TimelineEvent } from '@/approvals/types'
import { Icon } from '@/components/Icon'
import { asAmount, asCurrency, formatMoney, formatQuantity } from '@/format/money'
import { currentLocale } from '@/i18n'
import { RecordingPlayer, formatDuration } from '@/telephony/RecordingPlayer'
import { CallTranscript } from '@/ai/CallTranscript'
import { InboundReplyButton } from '@/mail/InboundReply'
import type { FeedItem } from './record'

/**
 * Мелочи, общие для раскладок контрагента и счёта (Company.dc.html, Invoice.dc.html).
 *
 * Не в `record/`: те компоненты не знают ни одной сущности и ни одного эндпоинта, а здесь —
 * путь раздела по навигации и форма события ленты сервера.
 */

export type Translate = (key: string, params?: Record<string, string | number>) => string

/** Путь списка сущности — из навигации, а не строкой: раздел могут переименовать. */
export function listPath(entity: string, fallback: string): string {
  const item = NAVIGATION.flatMap((section) => section.items).find((candidate) => candidate.listEntity === entity)
  return item?.path ?? fallback
}

/**
 * Копируемое значение (`.copyable`): ИНН, КПП, номер счёта диктуют по телефону и вставляют
 * в письма, и выделять их мышью из строки мета — ровно то, что макет убирает.
 */
export function CopyValue({ value, label, prefix }: { value: string; label: string; prefix?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className="copyable mono t-sm"
      style={{ border: 0, background: 'transparent', cursor: 'pointer' }}
      aria-label={label}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => setCopied(true), () => undefined)
      }}
    >
      {prefix ? `${prefix} ${value}` : value}
      <Icon name={copied ? 'check' : 'copy'} size="xs" />
    </button>
  )
}

/** Инициалы для `.ava`: «Петров Игорь» → «ПИ». */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/** Подпись ссылки списка (`<column>__label`) или null. */
export function labelOf(values: Record<string, unknown>, column: string): string | null {
  const label = values[`${column}__label`]
  return typeof label === 'string' && label !== '' ? label : null
}

/** Момент в поясе пользователя: дата и время вместе. */
export function formatMoment(value: string, locale: string): string {
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) return value
  return moment.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Суммы, сгруппированные по валюте.
 *
 * Деньги разных валют не складываются: рубли с евро дали бы цифру, которой нет, — то есть
 * курс один к одному. Порядок — по первому появлению, чтобы строка не прыгала.
 */
export function totalsByCurrency<T>(
  items: T[],
  currencyOf: (item: T) => string,
  amountOf: (item: T) => number,
): { currency: string; amount: number }[] {
  const totals = new Map<string, number>()
  for (const item of items) {
    const currency = currencyOf(item)
    totals.set(currency, round2((totals.get(currency) ?? 0) + amountOf(item)))
  }
  return [...totals.entries()].map(([currency, amount]) => ({ currency, amount }))
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

/** Событие единой ленты (#79) → строка `Feed`. */
export function feedItemOf(event: TimelineEvent, t: Translate): FeedItem {
  const kindLabel = t(`timeline.kind.${event.kind}`)
  // From a deal of the counterparty (#114): the number says whose stage, comment or file.
  const deal = event.details.dealNumber ? t('timeline.onDeal', { number: String(event.details.dealNumber) }) : undefined
  const author = event.actorName == null || event.actorName === '' ? undefined : event.actorName
  if (event.kind === 'stage') {
    return {
      id: event.id,
      kind: 'stage',
      at: event.at,
      title: t('record.feed.stage', {
        from: String(event.details.fromStage || t('timeline.stage.created')),
        to: String(event.details.toStage ?? ''),
      }),
      who: deal,
    }
  }
  if (event.kind === 'comment') {
    // The author by name: "a colleague's comment" is a question of who wrote it. Mentions
    // are the people the author called in — listed, so the reader sees who was asked.
    const mentions = Array.isArray(event.details.mentions)
      ? (event.details.mentions as { name?: unknown }[]).map((m) => `@${String(m.name ?? '')}`).filter((m) => m !== '@')
      : []
    return {
      id: event.id,
      kind: 'comment',
      at: event.at,
      title: kindLabel,
      who: [author, deal].filter(Boolean).join(' · ') || undefined,
      body: [event.body ?? '', mentions.length > 0 ? mentions.join(' ') : ''].filter(Boolean).join(' · ') || undefined,
    }
  }
  if (event.kind === 'document' || event.kind === 'approval') {
    // Issued paper: what it is and its number; for an approval — whose document and how it went.
    const type = String(event.details.documentType ?? '')
    const what = `${t(`timeline.document.${type}`)} ${event.subject ?? ''}`.trim()
    const status = String(event.details.status ?? '')
    // The feed has no hook for the language here, so the stored one; the formatter is the shared one (#91).
    const total = event.details.total
      ? formatMoney(asAmount(event.details.total), asCurrency(event.details.currency), currentLocale())
      : ''
    return {
      id: event.id,
      kind: event.kind,
      at: event.at,
      title: `${kindLabel}: ${what}`,
      who: author,
      body: [
        status ? t(event.kind === 'approval' ? `approvals.status.${status}` : `timeline.documentStatus.${status}`) : '',
        total,
      ]
        .filter(Boolean)
        .join(' · ') || undefined,
    }
  }
  if (event.kind === 'attachment') {
    const scan = String(event.details.scanStatus ?? '')
    return {
      id: event.id,
      kind: 'attachment',
      at: event.at,
      title: `${kindLabel}: ${event.subject ?? ''}`,
      who: [author, deal].filter(Boolean).join(' · ') || undefined,
      body: scan ? t(`timeline.scan.${scan}`) : undefined,
    }
  }
  if (event.kind === 'movement') {
    // A warehouse document's movement: what moved, how much, from where to where. Labels
    // come with the event; the cost of the movement does not, it is a different right.
    const details = event.details
    const what = String(details.productName || details.sku || '')
    const from = String(details.fromCellCode || '')
    const to = String(details.toCellCode || '')
    return {
      id: event.id,
      kind: 'movement',
      at: event.at,
      title: `${kindLabel}: ${t(`timeline.movement.${String(details.movementType ?? event.subject ?? '')}`)}`,
      body: [what, details.quantity != null ? formatQuantity(asAmount(details.quantity), currentLocale()) : '', from || to ? `${from || '—'} → ${to || '—'}` : '']
        .filter(Boolean)
        .join(' · '),
    }
  }
  if (event.kind === 'email' && event.details.direction === 'inbound') {
    // A received letter (#121): who wrote, whether it answers our letter, and its files with
    // the antivirus verdict — a file still being scanned must not read as one ready to open.
    // A letter on a card can be answered from the feed, with an AI draft (#134).
    const text = inboundBody(event, t)
    const inboundId = event.details.inboundId == null ? '' : String(event.details.inboundId)
    return {
      id: event.id,
      kind: 'mail',
      at: event.at,
      title: `${t('mail.feed.inbound')}: ${event.subject ?? ''}`.trim(),
      who: inboundSender(event) || undefined,
      body: inboundId
        ? (
            <span className="stack" style={{ gap: 4 }}>
              {text && <span>{text}</span>}
              <span><InboundReplyButton inboundId={inboundId} /></span>
            </span>
          )
        : text || undefined,
    }
  }
  if (event.kind === 'email' && event.details.delivery) {
    // An outbound letter (#117) says what happened to it: sent, queued for a retry, or the
    // reason it was refused. Without the word a refused letter would read as a sent one.
    const delivery = String(event.details.delivery)
    const reason = String(event.details.deliveryReason || '')
    const to = String(event.details.to || '')
    const status = delivery === 'rejected' && reason
      ? t('mail.feed.rejectedBecause', { reason })
      : delivery === 'failed' && reason
        ? t('mail.feed.failedBecause', { reason })
        : t(`mail.feed.${delivery}`)
    return {
      id: event.id,
      kind: 'mail',
      at: event.at,
      title: event.subject ? `${kindLabel}: ${event.subject}` : kindLabel,
      who: to || undefined,
      body: [status, event.body ?? ''].filter(Boolean).join(' · '),
    }
  }
  if (event.kind === 'change') {
    return { id: event.id, kind: 'change', at: event.at, title: `${kindLabel}: ${t(`timeline.change.${event.subject ?? ''}`)}` }
  }
  if (event.kind === 'message') {
    // A messenger message (#125): which channel and who wrote; our answer says what happened to
    // it — "sent" on a message that went nowhere would have the manager wait for a reply to a
    // question the client never saw.
    const channel = t(`messenger.channel.${String(event.details.channel ?? '')}`)
    const inbound = event.details.direction === 'inbound'
    const delivery = String(event.details.delivery ?? '')
    const reason = String(event.details.deliveryReason || '')
    const status = inbound || !delivery
      ? ''
      : delivery === 'rejected' && reason
        ? `${t('messenger.delivery.rejected')}: ${reason}`
        : t(`messenger.delivery.${delivery}`)
    return {
      id: event.id,
      kind: 'message',
      at: event.at,
      title: `${channel} · ${t(`messenger.direction.${inbound ? 'inbound' : 'outbound'}`)}`,
      who: inbound ? (event.details.from ? String(event.details.from) : undefined) : author,
      body: [event.body ?? '', status].filter(Boolean).join(' · ') || undefined,
    }
  }
  if (event.kind === 'call') {
    // A call (#122) says which way, how long and — when the PBX recorded it — offers the
    // recording. "A call" without its length reads the same for a minute and for an hour.
    return {
      id: event.id,
      kind: 'call',
      at: event.at,
      title: event.subject ? `${kindLabel}: ${event.subject}` : kindLabel,
      who: author,
      body: <CallBody event={event} t={t} />,
    }
  }
  const kind = event.kind === 'email' ? 'mail' : (event.kind as FeedItem['kind'])
  if (event.kind === 'meeting' && event.details.scheduledAt) {
    // A meeting in the feed says when and where (#112): "a meeting" without its time reads
    // as one that already happened.
    const start = new Date(String(event.details.scheduledAt))
    const end = event.details.endsAt ? new Date(String(event.details.endsAt)) : null
    const hm = (date: Date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    const when = `${start.toLocaleDateString()} ${hm(start)}${end ? `–${hm(end)}` : ''}`
    return {
      id: event.id,
      kind,
      at: event.at,
      title: event.subject ? `${kindLabel}: ${event.subject}` : kindLabel,
      body: [when, String(event.details.location || ''), event.body ?? ''].filter(Boolean).join(' · '),
    }
  }
  return {
    id: event.id,
    kind,
    at: event.at,
    title: event.subject ? `${kindLabel}: ${event.subject}` : kindLabel,
    who: author,
    body: event.body ?? undefined,
  }
}

/** Направление, длительность, текст и запись звонка — тело события ленты. */
export function CallBody({ event, t }: { event: TimelineEvent; t: Translate }) {
  const direction = event.details.direction == null || event.details.direction === ''
    ? ''
    : t(`telephony.direction.${String(event.details.direction)}`)
  const seconds = event.details.durationSeconds
  const duration = typeof seconds === 'number' ? formatDuration(seconds, t) : ''
  const meta = [direction, duration].filter(Boolean).join(' · ')
  const path = event.details.recordingPath == null ? null : String(event.details.recordingPath)
  const link = event.details.recordingUrl == null ? null : String(event.details.recordingUrl)
  if (!meta && !event.body && !path && !link) return null
  return (
    <span className="stack" style={{ gap: 4 }}>
      {event.body && <span>{event.body}</span>}
      {meta && <span className="t-sm muted">{meta}</span>}
      {path && <RecordingPlayer path={path} />}
      {/* A recording of our PBX can be transcribed and summarised (#130). */}
      {path && <CallTranscript activityId={event.id} />}
      {!path && link && (
        <a href={link} target="_blank" rel="noreferrer noopener">{t('telephony.recording.link')}</a>
      )}
    </span>
  )
}

/** «Имя <адрес>» отправителя входящего письма, или просто адрес. */
export function inboundSender(event: TimelineEvent): string {
  const from = event.details.from == null ? '' : String(event.details.from)
  const name = event.details.fromName == null ? '' : String(event.details.fromName)
  return name ? `${name} <${from}>` : from
}

/** Признак ответа, текст и файлы входящего письма — одной строкой для ленты. */
export function inboundBody(event: TimelineEvent, t: (key: string, params?: Record<string, string | number>) => string): string {
  const files = Array.isArray(event.details.attachments)
    ? (event.details.attachments as { fileName?: string; scanStatus?: string }[])
    : []
  return [
    event.details.inReplyTo != null && event.details.inReplyTo !== '' ? t('mail.feed.reply') : '',
    event.body ?? '',
    files.length > 0
      ? t('mail.feed.files', {
          files: files.map((file) => `${file.fileName ?? ''} (${t(`timeline.scan.${file.scanStatus ?? 'pending'}`)})`).join(', '),
        })
      : '',
  ]
    .filter(Boolean)
    .join(' · ')
}
